import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
  type CalendarDue,
  type CalendarPost,
  type CalendarView,
  fetchCalendar,
  updateCardFields,
  updatePost,
} from '../api.ts';
import { DASHBOARD_POLL_MS, describeError, redirectIfUnauthorised } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { useToast } from '../toast.tsx';
import { Segmented } from '../ui.tsx';
import * as wording from '../wording.ts';
import { dueChip, postChip, postMoment, postPlatformLabel, shortDay } from './lines.ts';
import { cardTitle, type ContentCard } from './model.ts';
import {
  CALENDAR_MODES,
  type CalendarMode,
  type CalendarPeriod,
  dayKey,
  onDay,
  periodDays,
  periodRange,
  periodTitle,
  WEEKDAY_LABELS,
} from './period.ts';

/**
 * The calendar view of the board (US-032): planned and published posts and
 * publish-by dates, by month or by week. It reads `GET /api/calendar` for the
 * days on show on the dashboard's beat; the board page owns the period (the
 * address), the filters (`keep`) and the drawer a chip opens.
 */

/** A month cell lists this many entries, then "+n more". */
const MONTH_CELL_ENTRIES = 4;

/** One thing on a day: a post at its moment, or a publish-by date. */
type Entry =
  | { readonly kind: 'due'; readonly key: string; readonly cardId: string; readonly due: CalendarDue }
  | { readonly kind: 'post'; readonly key: string; readonly cardId: string; readonly post: CalendarPost; readonly at: Date };

/**
 * The entries of each local day: publish-by dates first (they have no time),
 * then the posts by time, as the server ordered them. Posted posts stand on
 * the day they went out, planned ones on the day they are planned for. A
 * chip just dropped elsewhere stands where `moved` says (an entry key → the
 * new day of a publish-by date, or the new instant of a post).
 */
function entriesByDay(
  view: CalendarView,
  keep: (cardId: string) => boolean,
  moved: ReadonlyMap<string, string>,
): Map<string, Entry[]> {
  const days = new Map<string, Entry[]>();
  const add = (day: string, entry: Entry): void => {
    const list = days.get(day);
    if (list === undefined) days.set(day, [entry]);
    else list.push(entry);
  };
  for (const due of view.due) {
    const key = `due:${due.cardId}`;
    if (keep(due.cardId)) add(moved.get(key) ?? due.dueOn, { kind: 'due', key, cardId: due.cardId, due });
  }
  for (const post of view.posts) {
    const key = `post:${post.id}`;
    const cardId = `content:${post.contentId}`;
    const moment = moved.get(key) ?? (post.status === 'posted' ? post.postedAt : post.scheduledAt);
    if (moment === null || !keep(cardId)) continue;
    const at = new Date(moment);
    add(dayKey(at), { kind: 'post', key, cardId, post, at });
  }
  return days;
}

/** What is being dragged over the calendar (US-033). */
type Dragged =
  | { readonly kind: 'card'; readonly card: ContentCard }
  | { readonly kind: 'entry'; readonly entry: Entry };

/** The drag data type; the value is only a label, the calendar keeps the dragged thing itself. */
const DRAG_TYPE = 'application/x-mc-calendar';

/**
 * A chip dropped on another day stands there at once. It keeps that place
 * while its call runs and until the next read after it: a refused move goes
 * back on that read (`view` is no longer `seen`).
 */
interface Move {
  readonly to: string;
  readonly pending: boolean;
  readonly seen: CalendarView | null;
}

/**
 * The calendar's read, kept live like `useBoard`: on the dashboard's beat
 * while the tab is shown, at once when it is shown again, and again whenever
 * `refreshToken` changes (the board page bumps it after an action).
 */
function useCalendar(
  from: string,
  to: string,
  refreshToken: number,
): { view: CalendarView | null; error: string | null; latest: () => CalendarView | null } {
  const [view, setView] = useState<CalendarView | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The read on show, for callbacks that outlive the render they started in. */
  const shown = useRef<CalendarView | null>(null);
  const controller = useRef<AbortController | null>(null);
  const inFlight = useRef<AbortSignal | null>(null);
  const again = useRef(false);
  const range = useRef({ from, to });

  const load = useCallback(async (signal: AbortSignal, queue = false): Promise<void> => {
    if (inFlight.current !== null && !inFlight.current.aborted) {
      if (queue) again.current = true;
      return;
    }
    inFlight.current = signal;
    try {
      const answer = await fetchCalendar(range.current.from, range.current.to, { signal });
      shown.current = answer;
      setView(answer);
      setError(null);
    } catch (cause: unknown) {
      if (signal.aborted) return;
      if (redirectIfUnauthorised(cause)) return;
      setError(describeError(cause));
    } finally {
      if (inFlight.current === signal) inFlight.current = null;
    }
    if (again.current && !signal.aborted) {
      again.current = false;
      await load(signal);
    }
  }, []);

  useEffect(() => {
    // Another period: the old read is dropped, and nothing of it is drawn under the new title.
    range.current = { from, to };
    shown.current = null;
    setView(null);
    const current = new AbortController();
    controller.current = current;
    const visible = (): boolean => document.visibilityState === 'visible';

    void load(current.signal);
    const timer = window.setInterval(() => {
      if (visible()) void load(current.signal);
    }, DASHBOARD_POLL_MS);
    const onVisible = (): void => {
      if (visible()) void load(current.signal);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      current.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, from, to]);

  useEffect(() => {
    if (refreshToken === 0) return;
    const current = controller.current;
    if (current !== null && !current.signal.aborted) void load(current.signal, true);
  }, [load, refreshToken]);

  const latest = useCallback((): CalendarView | null => shown.current, []);
  return { view, error, latest };
}

export function Calendar({
  period,
  onPeriod,
  onMode,
  onToday,
  keep,
  openCardId,
  onOpen,
  refreshToken,
  toPost,
  onSchedule,
  onChanged,
}: {
  readonly period: CalendarPeriod;
  readonly onPeriod: (delta: 1 | -1) => void;
  readonly onMode: (mode: CalendarMode) => void;
  readonly onToday: () => void;
  /** Whether the filters keep the card an entry belongs to. */
  readonly keep: (cardId: string) => boolean;
  readonly openCardId: string | null;
  readonly onOpen: (cardId: string) => void;
  readonly refreshToken: number;
  /** The To post column, filtered and in its order: dropped on a day, one opens ScheduleDialog. */
  readonly toPost: readonly ContentCard[];
  readonly onSchedule: (card: ContentCard, day: string) => void;
  /** Reads the board and the calendar again after a chip moved. */
  readonly onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const { from, to } = periodRange(period);
  const { view, error, latest } = useCalendar(from, to, refreshToken);
  /** The month cells opened past their first entries, by day. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  // Drag and drop (US-033): what is dragged, the day under it, and the chips just moved.
  const dragged = useRef<Dragged | null>(null);
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [overDay, setOverDay] = useState<string | null>(null);
  const [moves, setMoves] = useState<ReadonlyMap<string, Move>>(() => new Map());
  const moved = new Map<string, string>();
  for (const [key, move] of moves) {
    if (move.pending || move.seen === view) moved.set(key, move.to);
  }
  const days = periodDays(period);
  const byDay = view === null ? new Map<string, Entry[]>() : entriesByDay(view, keep, moved);

  const startDrag = (event: DragEvent<HTMLElement>, what: Dragged, key: string): void => {
    event.dataTransfer.setData(DRAG_TYPE, key);
    event.dataTransfer.effectAllowed = 'move';
    dragged.current = what;
    setDraggedKey(key);
  };
  const endDrag = (): void => {
    dragged.current = null;
    setDraggedKey(null);
    setOverDay(null);
  };

  /** Moves a chip at once, runs `call`, and keeps it there until the read after the call. */
  const moveChip = async (key: string, target: string, call: () => Promise<string>): Promise<void> => {
    const place = (move: Move): void => {
      setMoves((previous) => new Map(previous).set(key, move));
    };
    place({ to: target, pending: true, seen: null });
    try {
      toast.ok(await call());
      await onChanged();
    } catch (cause: unknown) {
      toast.error(describeError(cause));
    } finally {
      place({ to: target, pending: false, seen: latest() });
    }
  };

  const drop = (what: Dragged, day: string): void => {
    if (what.kind === 'card') {
      onSchedule(what.card, day);
      return;
    }
    const { entry } = what;
    if (entry.kind === 'due') {
      if (entry.due.dueOn === day) return;
      void moveChip(entry.key, day, async () => {
        await updateCardFields(entry.cardId, { dueOn: day });
        return wording.calendarDueMoved(entry.due.title, shortDay(day));
      });
      return;
    }
    if (entry.post.status === 'posted' || dayKey(entry.at) === day) return;
    const at = onDay(entry.at, day);
    if (at === null) return;
    const scheduledAt = at.toISOString();
    void moveChip(entry.key, scheduledAt, async () => {
      await updatePost(entry.post.id, { scheduledAt });
      return wording.calendarPostMoved(entry.post.contentName, postMoment(scheduledAt));
    });
  };
  const now = Date.now();
  const today = dayKey(new Date(now));
  const month = period.mode === 'month' ? period.month - 1 : null;

  return (
    <section className={`calendar calendar--${period.mode}`} aria-label={wording.BOARD_VIEW_CALENDAR}>
      <div className="calendar__head">
        <div className="calendar__nav">
          <button
            type="button"
            className="button button--icon"
            aria-label={wording.CALENDAR_PREVIOUS}
            title={`${wording.CALENDAR_PREVIOUS} ([)`}
            onClick={() => {
              onPeriod(-1);
            }}
          >
            <Icon name="chevron-right" className="calendar__back" />
          </button>
          <button type="button" className="button" title={`${wording.CALENDAR_TODAY} (t)`} onClick={onToday}>
            {wording.CALENDAR_TODAY}
          </button>
          <button
            type="button"
            className="button button--icon"
            aria-label={wording.CALENDAR_NEXT}
            title={`${wording.CALENDAR_NEXT} (])`}
            onClick={() => {
              onPeriod(1);
            }}
          >
            <Icon name="chevron-right" />
          </button>
        </div>
        <h2 className="calendar__title" aria-live="polite">
          {periodTitle(period)}
        </h2>
        <Segmented ariaLabel={wording.CALENDAR_PERIOD} value={period.mode} onChange={onMode} options={CALENDAR_MODES} />
      </div>

      {error !== null && (
        <div className="notice notice--error" role="alert">
          <Icon name="x-circle" />
          <div className="notice__body">
            {wording.CALENDAR_READ_FAILED} {error}
          </div>
        </div>
      )}

      <div className="calendar__body">
        <div className="calendar__grid">
          {WEEKDAY_LABELS.map((label, index) => (
            <div key={label} className="calendar__weekday" aria-hidden="true">
              {period.mode === 'week' ? `${label} ${String(days[index]?.getDate() ?? '')}` : label}
            </div>
          ))}
          {days.map((day) => {
            const key = dayKey(day);
            const entries = byDay.get(key) ?? [];
            const open = period.mode === 'week' || expanded.has(key);
            const shown = open ? entries : entries.slice(0, MONTH_CELL_ENTRIES);
            const hidden = entries.length - shown.length;
            const classes = [
              'calendar__day',
              key === today ? 'calendar__day--today' : '',
              key < today ? 'calendar__day--past' : '',
              month !== null && day.getMonth() !== month ? 'calendar__day--outside' : '',
              key === overDay ? 'calendar__day--drop' : '',
            ].filter((name) => name !== '');
            return (
              <div
                key={key}
                className={classes.join(' ')}
                role="group"
                aria-label={day.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
                {...(key === today ? { 'aria-current': 'date' as const } : {})}
                onDragOver={(event) => {
                  // Only what the calendar itself started is taken.
                  if (dragged.current === null) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  if (overDay !== key) setOverDay(key);
                }}
                onDragLeave={(event) => {
                  if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
                  setOverDay((previous) => (previous === key ? null : previous));
                }}
                onDrop={(event) => {
                  const what = dragged.current;
                  if (what === null) return;
                  event.preventDefault();
                  endDrag();
                  drop(what, key);
                }}
              >
                {period.mode === 'month' && <span className="calendar__date">{day.getDate()}</span>}
                {shown.length > 0 && (
                  <ul className="calendar__entries">
                    {shown.map((entry) => (
                      <li key={entry.key}>
                        <EntryChip
                          entry={entry}
                          now={now}
                          open={entry.cardId === openCardId}
                          dragging={entry.key === draggedKey}
                          onOpen={onOpen}
                          onDragStart={(event) => {
                            startDrag(event, { kind: 'entry', entry }, entry.key);
                          }}
                          onDragEnd={endDrag}
                        />
                      </li>
                    ))}
                  </ul>
                )}
                {hidden > 0 && (
                  <button
                    type="button"
                    className="calendar__more"
                    onClick={() => {
                      setExpanded((previous) => new Set(previous).add(key));
                    }}
                  >
                    {wording.calendarMore(hidden)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <aside className="calendar__to-post" aria-label={wording.CALENDAR_TO_POST}>
          <h3 className="calendar__to-post-title">
            {wording.CALENDAR_TO_POST} <span className="calendar__to-post-count">{toPost.length}</span>
          </h3>
          {toPost.length === 0 ? (
            <p className="calendar__to-post-empty">{wording.CALENDAR_TO_POST_EMPTY}</p>
          ) : (
            <>
              <p className="calendar__to-post-hint">{wording.CALENDAR_TO_POST_HINT}</p>
              <ul className="calendar__entries">
                {toPost.map((card) => (
                  <li key={card.cardId}>
                    <button
                      type="button"
                      draggable
                      className={`badge badge--neutral calendar-chip calendar-chip--to-post${
                        card.cardId === openCardId ? ' calendar-chip--open' : ''
                      }${card.cardId === draggedKey ? ' calendar-chip--dragging' : ''}`}
                      data-card-id={card.cardId}
                      title={cardTitle(card)}
                      onClick={() => {
                        onOpen(card.cardId);
                      }}
                      onDragStart={(event) => {
                        startDrag(event, { kind: 'card', card }, card.cardId);
                      }}
                      onDragEnd={endDrag}
                    >
                      <Icon name="grabber" />
                      <span className="calendar-chip__text">{cardTitle(card)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

/** "09:00" in this browser's zone. */
function clock(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * One entry as a chip that opens its card's drawer: a post in the tones of
 * § Due dates (done with ✓ once posted, red when planned and past its time,
 * amber when its content is not delivered within 48 hours), or a publish-by
 * date with a dashed outline. Every chip but a posted post drags to another
 * day (US-033).
 */
function EntryChip({
  entry,
  now,
  open,
  dragging,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  readonly entry: Entry;
  readonly now: number;
  readonly open: boolean;
  readonly dragging: boolean;
  readonly onOpen: (cardId: string) => void;
  readonly onDragStart: (event: DragEvent<HTMLElement>) => void;
  readonly onDragEnd: () => void;
}) {
  const openClass = (open ? ' calendar-chip--open' : '') + (dragging ? ' calendar-chip--dragging' : '');
  const drag = { draggable: true, onDragStart, onDragEnd };
  if (entry.kind === 'due') {
    const tone = dueChip(entry.due.dueOn, false, now)?.tone ?? 'neutral';
    const text = wording.calendarDue(entry.due.title);
    return (
      <button
        type="button"
        className={`badge badge--${tone} calendar-chip calendar-chip--due${openClass}`}
        data-card-id={entry.cardId}
        title={text}
        {...drag}
        onClick={() => {
          onOpen(entry.cardId);
        }}
      >
        <Icon name="calendar" />
        <span className="calendar-chip__text">{text}</span>
      </button>
    );
  }
  const { post } = entry;
  const chip = postChip(post, post.contentStatus === 'delivered', now);
  const platform = postPlatformLabel(post.platform, post.label);
  const text = `${clock(entry.at)} ${platform} · ${post.contentName}`;
  return (
    <button
      type="button"
      className={`badge badge--${chip.posted ? 'done' : chip.tone} calendar-chip${openClass}`}
      data-card-id={entry.cardId}
      title={chip.title === undefined ? text : `${text} — ${chip.title}`}
      {...(chip.posted ? {} : drag)}
      onClick={() => {
        onOpen(entry.cardId);
      }}
    >
      <span className="calendar-chip__text">
        {clock(entry.at)} {platform}
        {post.paid && (
          <>
            {' '}
            <abbr className="calendar-chip__ad" title={wording.CALENDAR_PAID}>
              {wording.CALENDAR_AD}
            </abbr>
          </>
        )}{' '}
        · {post.contentName}
      </span>
      {chip.posted && <span aria-label={wording.BOARD_POSTED}>✓</span>}
    </button>
  );
}
